// src/view/vfx.ts — Havuzlu VFX (DESIGN §11, ARCHITECTURE §9): tek Points (kıvılcım + toz, özel shader: nokta başına boyut/renk/şekil),
// tek LineSegments (16 iz şeridi × 12 nokta, additive), halkalar InstancedMesh (kenar halkaları / şok halkası / tutulan halkası /
// eşya doğma + yeniden doğuş halkaları), kırılma parçaları InstancedMesh (8). Sahnede 4 draw call
// (belge: 3 havuz + 3 sabit mesh + 8 parça = 14'e karşı). Olay → efekt tablosu `onEvent` içinde; sıcak yolda tahsis yok.
// IŞIK SÜTUNU havuzu duruyor ama artık hiçbir olay kullanmıyor (kullanıcı geri bildirimi: eşya doğmadaki mavi, ring-out'taki ince
// kırmızı ve yeniden doğuştaki beyaz silindir "çok çirkin"). Silindirlerin yerini halka + kıvılcım efektleri aldı; havuz ilk
// `column()` çağrısında sahneye EKLENİR (kullanılmazsa boşa draw call harcamasın) — bkz. `column`.
import * as THREE from 'three';
import type { Fighter, Joint, SimEvent, SimWorld, Vec3 } from '../core/types.ts';
import { Ev, FState, HitKind, MovePhase } from '../core/types.ts';
import { clamp01 } from '../core/math.ts';
import { MAX_FIGHTERS, NO_ID, TICK_DT } from '../config/constants.ts';
import { T } from '../config/tuning.ts';

/* ------------------------------------------------------------- yerel sabitler (DESIGN §11 VFX satırı; tuning'de yok → needsWP0) */
export const SPARK_SIZE = { light: 0.3, mid: 0.5, finisher: 0.8, parry: 0.6, block: 0.35 } as const;
const SPARK_LIFE = 0.12;
const SPARK_COUNT = 32;
const DUST_COUNT = 200;
const DUST_LIFE = 0.3;
const DUST_PUFFS = 6;
const TRAIL_STRIPS = 16;
const TRAIL_POINTS = 12;
const TRAIL_LIFE = 0.10;
// Geçici halka slotu 4 → 6: eşya doğma ve yeniden doğuş artık İKİŞER halka kullanıyor (telegraf + varış); kenar alarmı halkalarıyla
// aynı anda 4 slot dolup efekt sessizce düşüyordu. Aynı InstancedMesh → draw call değişmez, yalnız 2 instance daha.
const RING_SLOTS = MAX_FIGHTERS + 6;          // kenar halkaları (dövüşçü başına) + 6 geçici (şok / kenar alarmı / doğuş)
const COLUMN_SLOTS = 4;
const COLUMN_LIFE = 0.8;
const FRAG_COUNT = 8;
const FRAG_LIFE = 0.7;
const SHOCK_RING_RADIUS = 2.2;               // Leap Smash halkası son yarıçapı (DESIGN §4.1 D2 halka r ≈ 2.2)
const SHOCK_RING_LIFE = 0.25;
/** Eşyanın 3 u'dan düşme süresi (s): t = √(2h/g) — telegraf halkası tam eşya yere değerken kapanır (DESIGN §9 dropHeight/dropGravity). */
const ITEM_FALL_SEC = Math.sqrt(2 * T.ITEMS.dropHeight / Math.abs(T.ITEMS.dropGravity));
/** Yeniden doğuşta ayakların yere değmesi: Spawn_Air'de kontrolün açıldığı tick (DESIGN §8) — halka tam o an kapanır. */
const RESPAWN_DROP_SEC = T.RESPAWN.controlAt * TICK_DT;
const COLOR_WHITE = 0xffffff, COLOR_ORANGE = 0xff9a3c, COLOR_YELLOW = 0xffe066, COLOR_BLUE = 0x6ec6ff, COLOR_RED = 0xff3b3b, COLOR_DUST = 0xc9bfa8;
/** Eşya = ödül → altın sarısı; ring-out közü sıcak turuncu; yeniden doğuş soğuk beyaz (mavi silindirin yerine geçen kimlik renkleri). */
const COLOR_GOLD = 0xffa32a, COLOR_EMBER = 0xff6a2c, COLOR_SPAWN = 0xa8dcff;

const enum Shape { Circle = 0, Star = 1 }

/* ------------------------------------------------------------- shader'lar */
const POINT_VERT = `
attribute vec4 aColor; attribute float aSize; attribute float aShape;
varying vec4 vColor; varying float vShape;
uniform float uScale;
void main() {
  vColor = aColor; vShape = aShape;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = aSize * uScale / max(0.1, -mv.z);
  gl_Position = projectionMatrix * mv;
}`;
const POINT_FRAG = `
varying vec4 vColor; varying float vShape;
void main() {
  vec2 p = gl_PointCoord * 2.0 - 1.0;
  float r = length(p);
  float a;
  if (vShape > 0.5) { float ang = atan(p.y, p.x); float star = 0.55 + 0.45 * abs(cos(ang * 2.5)); a = 1.0 - smoothstep(star * 0.6, star, r); }
  else { a = 1.0 - smoothstep(0.35, 1.0, r); }
  if (a <= 0.01) discard;
  gl_FragColor = vec4(vColor.rgb, vColor.a * a);
}`;
const LINE_VERT = `attribute vec4 aColor; varying vec4 vColor; void main(){ vColor = aColor; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`;
const LINE_FRAG = `varying vec4 vColor; void main(){ if (vColor.a <= 0.01) discard; gl_FragColor = vColor; }`;

/* ------------------------------------------------------------- havuz kayıtları */
interface Particle { life: number; max: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; size: number; color: number; shape: number; grav: number }
const newParticle = (): Particle => ({ life: 0, max: 1, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, size: 0.3, color: COLOR_WHITE, shape: 0, grav: 0 });
interface Trail { fighter: number; joint: Joint; emitting: boolean; pts: Float32Array; ages: Float32Array; head: number; count: number; color: number }
/** `delay`: halka bu süre boyunca GİZLİ bekler, sonra yaşamaya başlar — tek olaydan "telegraf + varış" (eşya iner / doğuş oturur)
 *  iki vuruşlu efekt çıkarmanın tahsissiz yolu (zamanlayıcı yok, slot zaten ayrılmış). `mode`: alfa eğrisi (RingAlpha). */
interface Ring { kind: number; life: number; max: number; delay: number; mode: number; x: number; y: number; z: number; r0: number; r1: number; color: number; alpha: number }
interface Column { life: number; max: number; x: number; y: number; z: number; r: number; h: number; color: number }
interface Frag { life: number; max: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; rx: number; ry: number }

const enum RingKind { None = 0, Edge = 1, Shock = 2, Held = 3, Dust = 4, Spawn = 5 }
/** Halka alfa eğrisi: Fade = doğar doğmaz sönen (darbe/şok), Grow = daralırken PARLAYAN (bir şeyin geldiğini haber veren telegraf). */
const enum RingAlpha { Fade = 0, Grow = 1 }

export interface VfxDeps {
  scene: THREE.Object3D;
  playerId: number;
  /** Aynı ekranda 1–4 yerel oyuncu (config/players): kenar halkası UYARISI hepsine güçlü gitsin (verilmezse `id === playerId`).
   *  Neden gerekli: "düşmek üzeresin" halkası oyuncunun kendi halkasıdır; P2 onu rakip gibi soluk görürse uyarıyı kaçırır. */
  isLocal?: (id: number) => boolean;
  /** Dövüşçünün görünüm konumu (interpolasyonlu); false → yok. */
  posOf: (id: number, out: Vec3) => boolean;
  /** Eklem dünya konumu (iz şeridi; AnimController.jointWorld). */
  jointWorld: (id: number, joint: Joint, out: Vec3) => boolean;
  /** Beyaz flaş (TumbleControl 60 ms) — fighterView.flash. */
  flash: (id: number, sec: number) => void;
}

const _p: Vec3 = { x: 0, y: 0, z: 0 };
const _m = new THREE.Matrix4();
const _c = new THREE.Color();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _v = new THREE.Vector3();
const _e = new THREE.Euler();

export class Vfx {
  readonly points: THREE.Points;
  readonly lines: THREE.LineSegments;
  readonly rings: THREE.InstancedMesh;
  readonly columns: THREE.InstancedMesh;
  readonly frags: THREE.InstancedMesh;
  private readonly sparks: Particle[] = [];
  private readonly dust: Particle[] = [];
  private readonly trails: Trail[] = [];
  private readonly ringSlots: Ring[] = [];
  private readonly colSlots: Column[] = [];
  private readonly fragSlots: Frag[] = [];
  private readonly pPos: Float32Array; private readonly pCol: Float32Array; private readonly pSize: Float32Array; private readonly pShape: Float32Array;
  private readonly lPos: Float32Array; private readonly lCol: Float32Array;
  private readonly pointMat: THREE.ShaderMaterial;
  private time = 0;
  /** Büyük efekt kilidi: bir anda tek "büyük" (finisher/ringout) — DESIGN §11. */
  private bigUntil = 0;
  /** Test/istatistik: son karede canlı sayılar. */
  readonly live = { sparks: 0, dust: 0, trails: 0, rings: 0, columns: 0, frags: 0 };

  constructor(readonly deps: VfxDeps) {
    const total = SPARK_COUNT + DUST_COUNT;
    for (let i = 0; i < SPARK_COUNT; i++) this.sparks.push(newParticle());
    for (let i = 0; i < DUST_COUNT; i++) this.dust.push(newParticle());
    this.pPos = new Float32Array(total * 3); this.pCol = new Float32Array(total * 4); this.pSize = new Float32Array(total); this.pShape = new Float32Array(total);
    const pg = new THREE.BufferGeometry();
    pg.setAttribute('position', new THREE.BufferAttribute(this.pPos, 3).setUsage(THREE.DynamicDrawUsage));
    pg.setAttribute('aColor', new THREE.BufferAttribute(this.pCol, 4).setUsage(THREE.DynamicDrawUsage));
    pg.setAttribute('aSize', new THREE.BufferAttribute(this.pSize, 1).setUsage(THREE.DynamicDrawUsage));
    pg.setAttribute('aShape', new THREE.BufferAttribute(this.pShape, 1).setUsage(THREE.DynamicDrawUsage));
    this.pointMat = new THREE.ShaderMaterial({
      vertexShader: POINT_VERT, fragmentShader: POINT_FRAG, uniforms: { uScale: { value: 600 } },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    this.points = new THREE.Points(pg, this.pointMat);
    this.points.frustumCulled = false; this.points.name = 'vfx_points'; this.points.renderOrder = 10;

    const segs = TRAIL_STRIPS * (TRAIL_POINTS - 1);
    this.lPos = new Float32Array(segs * 2 * 3); this.lCol = new Float32Array(segs * 2 * 4);
    const lg = new THREE.BufferGeometry();
    lg.setAttribute('position', new THREE.BufferAttribute(this.lPos, 3).setUsage(THREE.DynamicDrawUsage));
    lg.setAttribute('aColor', new THREE.BufferAttribute(this.lCol, 4).setUsage(THREE.DynamicDrawUsage));
    const lm = new THREE.ShaderMaterial({ vertexShader: LINE_VERT, fragmentShader: LINE_FRAG, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    this.lines = new THREE.LineSegments(lg, lm);
    this.lines.frustumCulled = false; this.lines.name = 'vfx_trails'; this.lines.renderOrder = 10;
    for (let i = 0; i < TRAIL_STRIPS; i++) this.trails.push({ fighter: NO_ID, joint: 'hand.r', emitting: false, pts: new Float32Array(TRAIL_POINTS * 3), ages: new Float32Array(TRAIL_POINTS), head: 0, count: 0, color: COLOR_WHITE });

    // Halkalar: yassı ring; additive → renk·alpha ile solma; ölçek 0 = görünmez
    const ringGeo = new THREE.RingGeometry(0.86, 1, 28).rotateX(-Math.PI / 2);
    const ringMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, side: THREE.DoubleSide });
    this.rings = new THREE.InstancedMesh(ringGeo, ringMat, RING_SLOTS);
    this.rings.frustumCulled = false; this.rings.name = 'vfx_rings'; this.rings.renderOrder = 5;
    for (let i = 0; i < RING_SLOTS; i++) this.ringSlots.push({ kind: RingKind.None, life: 0, max: 1, delay: 0, mode: RingAlpha.Fade, x: 0, y: 0, z: 0, r0: 1, r1: 1, color: COLOR_RED, alpha: 1 });

    const colGeo = new THREE.CylinderGeometry(1, 1, 1, 12, 1, true).translate(0, 0.5, 0);
    const colMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, side: THREE.DoubleSide });
    this.columns = new THREE.InstancedMesh(colGeo, colMat, COLUMN_SLOTS);
    this.columns.frustumCulled = false; this.columns.name = 'vfx_columns'; this.columns.renderOrder = 5;
    for (let i = 0; i < COLUMN_SLOTS; i++) this.colSlots.push({ life: 0, max: 1, x: 0, y: 0, z: 0, r: 0.5, h: 4, color: COLOR_WHITE });

    const fragGeo = new THREE.BoxGeometry(0.12, 0.12, 0.12);
    const fragMat = new THREE.MeshStandardMaterial({ color: 0x9aa4b8, roughness: 0.8 });
    this.frags = new THREE.InstancedMesh(fragGeo, fragMat, FRAG_COUNT);
    this.frags.frustumCulled = false; this.frags.name = 'vfx_frags'; this.frags.castShadow = false;
    for (let i = 0; i < FRAG_COUNT; i++) this.fragSlots.push({ life: 0, max: 1, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, rx: 0, ry: 0 });

    // İlk yazım: her şey gizli
    this.rings.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(RING_SLOTS * 3), 3);
    this.columns.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(COLUMN_SLOTS * 3), 3);
    for (let i = 0; i < RING_SLOTS; i++) this.rings.setMatrixAt(i, _m.makeScale(0, 0, 0));
    for (let i = 0; i < COLUMN_SLOTS; i++) this.columns.setMatrixAt(i, _m.makeScale(0, 0, 0));
    for (let i = 0; i < FRAG_COUNT; i++) this.frags.setMatrixAt(i, _m.makeScale(0, 0, 0));
    // Sütun havuzu SAHNEYE EKLENMEZ: hiçbir olay artık sütun kullanmıyor, boş bir InstancedMesh de draw call harcar
    // (instance'lar 0 ölçekli olsa bile çizim çağrısı gider). İlk `column()` çağrısında eklenir.
    deps.scene.add(this.points, this.lines, this.rings, this.frags);
  }

  /** Nokta boyutu için ekran yüksekliği (piksel) — resize'da WP9 çağırır. */
  setViewportHeight(px: number): void { this.pointMat.uniforms.uScale!.value = px * 0.5; }
  /** Sahnedeki VFX çizim çağrısı sayısı: 4 (nokta + iz + halka + parça); sütun havuzu kullanılırsa 5. */
  get drawCalls(): number { return this.columns.parent ? 5 : 4; }

  /* ---------------------------------------------------------------- yayıcılar */

  private spark(x: number, y: number, z: number, size: number, color: number, shape: number, n = 1, spread = 0): void {
    for (let k = 0; k < n; k++) {
      let best: Particle | null = null;
      for (const p of this.sparks) if (p.life <= 0) { best = p; break; }
      if (!best) { best = this.sparks[0]!; for (const p of this.sparks) if (p.life < best.life) best = p; }
      const a = Math.random() * Math.PI * 2, r = spread * Math.random();
      best.life = best.max = SPARK_LIFE; best.x = x + Math.cos(a) * r; best.y = y + (Math.random() - 0.5) * spread; best.z = z + Math.sin(a) * r;
      best.vx = Math.cos(a) * spread * 6; best.vy = spread * 3; best.vz = Math.sin(a) * spread * 6;
      best.size = size; best.color = color; best.shape = shape; best.grav = 0;
    }
  }
  /** Toz halkası: ayak çevresine 6 puf (DESIGN §11). */
  private dustRing(x: number, y: number, z: number, n = DUST_PUFFS, speed = 2.2): void {
    let emitted = 0;
    for (const p of this.dust) {
      if (p.life > 0) continue;
      const a = (emitted / n) * Math.PI * 2 + Math.random() * 0.4;
      p.life = p.max = DUST_LIFE; p.x = x; p.y = y + 0.1; p.z = z;
      p.vx = Math.cos(a) * speed; p.vy = 0.8 + Math.random() * 0.6; p.vz = Math.sin(a) * speed;
      p.size = 0.35 + Math.random() * 0.2; p.color = COLOR_DUST; p.shape = Shape.Circle; p.grav = -4;
      if (++emitted >= n) break;
    }
  }
  private ring(kind: RingKind, x: number, y: number, z: number, r0: number, r1: number, life: number, color: number, alpha: number, delay = 0, mode: RingAlpha = RingAlpha.Fade): Ring | null {
    // Dövüşçü kenar halkaları ilk MAX_FIGHTERS slotta; geçiciler sonda
    let slot: Ring | null = null;
    for (let i = MAX_FIGHTERS; i < RING_SLOTS; i++) { const r = this.ringSlots[i]!; if (r.kind === RingKind.None || r.life <= 0) { slot = r; break; } }
    if (!slot) return null;
    slot.kind = kind; slot.x = x; slot.y = y; slot.z = z; slot.r0 = r0; slot.r1 = r1; slot.life = slot.max = life; slot.color = color; slot.alpha = alpha;
    slot.delay = delay; slot.mode = mode;
    return slot;
  }
  /**
   * Kıvılcım/köz: YERÇEKİMLİ havuzdan (toz havuzuyla aynı Points — ayrı draw call yok), olay başına renk/ömür/hız verilebilir.
   * `rad` doğma yarıçapı, `vOut` radyal hız (negatif = merkeze toplanır), `vy` dikey hız, `grav` ivme, `shape` daire (sönerken küçülür)
   * ya da yıldız (sönerken büyür → parıltı). Sütunların yerine gelen "iniş kıvılcımı" efektlerinin ortak yayıcısı; tahsis yok.
   */
  private embers(x: number, y: number, z: number, n: number, color: number, size: number, life: number, rad: number, vOut: number, vy: number, grav: number, shape: Shape): void {
    let emitted = 0;
    for (const p of this.dust) {
      if (p.life > 0) continue;
      const a = (emitted / n) * Math.PI * 2 + Math.random() * 0.8, r = rad * (0.4 + 0.6 * Math.random());
      p.life = p.max = life;
      p.x = x + Math.cos(a) * r; p.y = y + (Math.random() - 0.5) * 0.3; p.z = z + Math.sin(a) * r;
      p.vx = Math.cos(a) * vOut; p.vy = vy * (0.8 + 0.4 * Math.random()); p.vz = Math.sin(a) * vOut;
      p.size = size * (0.75 + 0.5 * Math.random()); p.color = color; p.shape = shape; p.grav = grav;
      if (++emitted >= n) break;
    }
  }
  /** Işık sütunu (KULLANIMDA DEĞİL — dosya başlığındaki not): havuz ilk çağrıda sahneye girer, o ana kadar draw call harcamaz. */
  private column(x: number, y: number, z: number, r: number, h: number, color: number, life = COLUMN_LIFE): void {
    if (!this.columns.parent) this.deps.scene.add(this.columns);
    let slot = this.colSlots[0]!;
    for (const c of this.colSlots) if (c.life <= 0) { slot = c; break; }
    slot.life = slot.max = life; slot.x = x; slot.y = y; slot.z = z; slot.r = r; slot.h = h; slot.color = color;
  }
  private breakFragments(x: number, y: number, z: number): void {
    for (const f of this.fragSlots) {
      const a = Math.random() * Math.PI * 2, s = 2 + Math.random() * 2;
      f.life = f.max = FRAG_LIFE; f.x = x; f.y = y; f.z = z;
      f.vx = Math.cos(a) * s; f.vy = 3 + Math.random() * 3; f.vz = Math.sin(a) * s; f.rx = Math.random() * 6; f.ry = Math.random() * 6;
    }
  }
  private startTrail(fighter: number, joint: Joint, color: number): void {
    for (const t of this.trails) if (t.fighter === fighter && t.emitting) return;
    let slot: Trail | null = null;
    for (const t of this.trails) if (t.fighter === NO_ID) { slot = t; break; }
    if (!slot) return;
    slot.fighter = fighter; slot.joint = joint; slot.emitting = true; slot.head = 0; slot.count = 0; slot.color = color;
  }

  /* ---------------------------------------------------------------- olay tablosu */

  /** Olay → efekt (ARCHITECTURE §9 tablosu). Sarsıntı camera.ts'de, ses audio.ts'de; burada yalnız görsel. */
  onEvent(e: SimEvent, world: SimWorld): void {
    const big = this.time < this.bigUntil;
    switch (e.type) {
      case Ev.Hit: {
        const kind = e.v2 as HitKind;
        this.hitPoint(e, world);
        if (kind === HitKind.Finisher || kind === HitKind.Tumble) {
          if (!big) { this.spark(_p.x, _p.y, _p.z, SPARK_SIZE.finisher, COLOR_YELLOW, Shape.Star, 1); this.bigUntil = this.time + 0.15; }
          if (this.deps.posOf(e.target, _p)) this.dustRing(_p.x, _p.y, _p.z);
        } else if (kind === HitKind.Mid) this.spark(_p.x, _p.y, _p.z, SPARK_SIZE.mid, COLOR_ORANGE, Shape.Circle, 3, 0.15);
        else this.spark(_p.x, _p.y, _p.z, SPARK_SIZE.light, COLOR_WHITE, Shape.Circle, 2, 0.1);
        break;
      }
      case Ev.Blocked: this.hitPoint(e, world); this.spark(_p.x, _p.y, _p.z, SPARK_SIZE.block, COLOR_BLUE, Shape.Circle, 2, 0.12); break;
      case Ev.Parry: this.hitPoint(e, world); this.spark(_p.x, _p.y, _p.z, SPARK_SIZE.parry, COLOR_BLUE, Shape.Star, 1); this.deps.flash(e.actor, T.FEEL.flashMs / 1000); break;
      case Ev.GuardBreak: this.hitPoint(e, world); this.spark(_p.x, _p.y, _p.z, SPARK_SIZE.mid, COLOR_BLUE, Shape.Star, 2, 0.2); break;
      case Ev.TumbleControl: this.deps.flash(e.actor, T.FEEL.controlFlashMs / 1000); break;
      case Ev.Knockdown: case Ev.Land: case Ev.Dodge: case Ev.Lying: case Ev.BodyImpact:
        if (this.deps.posOf(e.actor, _p)) this.dustRing(_p.x, _p.y, _p.z);
        break;
      case Ev.PhaseChange: {
        // Active'e giriş: ağır hamlede iz şeridi; yerdeki halka hitbox'ı (Leap Smash) → şok halkası
        const f = world.fighters[e.actor]; const mv = world.moves[e.move >= 0 ? e.move : (f?.moveIdx ?? -1)];
        if (!f || !mv || f.phase !== MovePhase.Active) break;
        if (mv.heavy) {
          const hb = mv.hitboxes[0];
          const joint: Joint = hb ? (hb.joint === 'center' ? 'hand.r' : hb.joint) : 'hand.r';
          this.startTrail(f.id, f.weapon !== 'unarmed' && (joint === 'hand.r') ? 'weapon.tip' : joint, COLOR_ORANGE);
        }
        // groundedOnly hitbox araması for ile (olay yolunda closure yok)
        let groundR = -1;
        for (let k = 0; k < mv.hitboxes.length; k++) { const h = mv.hitboxes[k]!; if (h.groundedOnly) { groundR = h.radius; break; } }
        if (groundR >= 0) { this.ring(RingKind.Shock, f.pos.x, f.pos.y + 0.02, f.pos.z, 0.3, Math.max(groundR, SHOCK_RING_RADIUS), SHOCK_RING_LIFE, COLOR_YELLOW, 1); this.dustRing(f.pos.x, f.pos.y, f.pos.z, 12, 4); }
        break;
      }
      case Ev.Fell: {
        // Düşüş noktasından AŞAĞI savrulan kısa köz izi (eski "ince kırmızı sütun" çirkindi ve boşlukta asılı kalıyordu).
        // Olay noktası platformun DIŞINDA (kill plane) → yere yatan halka havada asılı görünürdü; bu yüzden yalnız düşen kıvılcım.
        // Ring-out'un asıl okunurluğu kamera sarsıntısı + ekran kenarı kırmızı flaşı (hud.ts) + "RING OUT" bandında; burada abartma.
        const y = world.arena.platforms[0]?.topY ?? 0;
        this.embers(e.x, y - 0.3, e.z, 10, COLOR_EMBER, 0.46, 0.42, 0.35, 0.7, -7, -22, Shape.Circle);
        this.bigUntil = this.time + 0.3;
        break;
      }
      case Ev.ItemSpawn: {
        // Silah geliyor (DESIGN §9: 3 u yükseklikten düşer). Sütun yerine: eşyanın DÜŞÜŞ SÜRESİNCE daralıp parlayan altın zemin
        // halkası (nereye ineceğini önceden okutur — kenara koşma kararı verilebilir) + yukarıdan süzülen kıvılcımlar +
        // tam iniş anında (delay = düşüş süresi) küçük bir açılma halkası. Üçü de var olan halka/nokta havuzundan; yeni draw call yok.
        // Alfa 1'e yakın tutulmuyor: halkalar ADDITIVE ve arena zemini açık mavi — yüksek alfa doyup BEYAZA kaçıyor (ölçüm:
        // ekran görüntüsünde altın ton kayboluyordu). 0.55–0.8 aralığında renk zeminin üstünde sıcak kalıyor.
        const gy = e.y - T.ITEMS.dropHeight;   // olay y'si DOĞMA yüksekliği; halka yere ait
        this.ring(RingKind.Spawn, e.x, gy + 0.02, e.z, 2.1, 0.7, ITEM_FALL_SEC, COLOR_GOLD, 0.55, 0, RingAlpha.Grow);
        this.ring(RingKind.Spawn, e.x, gy + 0.02, e.z, 0.5, 2.1, 0.3, COLOR_GOLD, 0.8, ITEM_FALL_SEC, RingAlpha.Fade);
        this.embers(e.x, e.y - 0.2, e.z, 9, COLOR_GOLD, 0.42, ITEM_FALL_SEC, 0.6, -0.9, -1.5, -4, Shape.Star);
        break;
      }
      case Ev.Respawn: {
        // Yeniden doğuş (DESIGN §8 Spawn_Air ile yukarıdan iner): beyaz sütun yerine daralıp parlayan soğuk halka + üstten düşen
        // kıvılcımlar; ayak yere değerken (delay) açılan ikinci halka + toz. Karakter artık ışığın içinde kaybolmuyor.
        // Konum OLAYDAN okunur (match.ts respawn(): e.x/y/z = spawn noktası), `posOf`tan DEĞİL: olaylar `v.update()`ten ÖNCE
        // tüketiliyor, yani görünüm hâlâ bir önceki karenin — DÜŞÜŞ — konumunu taşıyor. Doğrulama turunda ölçüldü: efekt
        // platformun 8 u altında, ölüm noktasında doğuyordu (eski beyaz sütun da aynı yerde doğuyordu; kullanıcının
        // "dibe inince çıkan beyaz silindir" dediği buydu). Artık karakterin belirdiği spawn noktasında.
        this.ring(RingKind.Spawn, e.x, e.y + 0.02, e.z, 2.4, 0.85, RESPAWN_DROP_SEC, COLOR_SPAWN, 0.5, 0, RingAlpha.Grow);
        this.ring(RingKind.Spawn, e.x, e.y + 0.02, e.z, 0.6, 2.2, 0.3, COLOR_SPAWN, 0.75, RESPAWN_DROP_SEC, RingAlpha.Fade);
        this.embers(e.x, e.y + 2.4, e.z, 9, COLOR_SPAWN, 0.4, RESPAWN_DROP_SEC, 0.7, -0.8, -3.4, -2, Shape.Star);
        break;
      }
      case Ev.ItemBreak: this.breakFragments(e.x, e.y, e.z); break;
      case Ev.EdgeAlarm: if (this.deps.posOf(e.actor, _p)) this.ring(RingKind.Dust, _p.x, _p.y + 0.02, _p.z, 0.6, 1.6, 0.35, COLOR_RED, 1); break;
      default: break;
    }
  }
  /** İsabet noktası: olayın (x,y,z)'si; sıfırsa hedef göğüs hizası. */
  private hitPoint(e: SimEvent, world: SimWorld): void {
    if (e.x !== 0 || e.y !== 0 || e.z !== 0) { _p.x = e.x; _p.y = e.y; _p.z = e.z; return; }
    const t = world.fighters[e.target];
    if (t) { _p.x = t.pos.x; _p.y = t.pos.y + 1.2; _p.z = t.pos.z; } else { _p.x = _p.y = _p.z = 0; }
  }

  /* ---------------------------------------------------------------- kare */

  /** Parçacığı GPU tamponlarına yazar (özel metot: her karede closure yaratılmasın). */
  private writeParticle(p: Particle, idx: number): void {
    const o3 = idx * 3, o4 = idx * 4;
    if (p.life <= 0) { this.pSize[idx] = 0; this.pCol[o4 + 3] = 0; return; }
    const k = p.life / p.max;
    this.pPos[o3] = p.x; this.pPos[o3 + 1] = p.y; this.pPos[o3 + 2] = p.z;
    _c.setHex(p.color);
    this.pCol[o4] = _c.r; this.pCol[o4 + 1] = _c.g; this.pCol[o4 + 2] = _c.b; this.pCol[o4 + 3] = k;
    this.pSize[idx] = p.size * (p.shape === Shape.Star ? 1 + (1 - k) * 0.8 : 0.7 + k * 0.5);
    this.pShape[idx] = p.shape;
  }

  update(dt: number, world: SimWorld): void {
    this.time += dt;
    // Parçacıklar
    let i = 0, live = 0, liveDust = 0;
    for (const p of this.sparks) { if (p.life > 0) { p.life -= dt; p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt; if (p.life > 0) live++; } this.writeParticle(p, i++); }
    for (const p of this.dust) { if (p.life > 0) { p.life -= dt; p.vy += p.grav * dt; p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt; p.vx *= 0.9; p.vz *= 0.9; if (p.life > 0) liveDust++; } this.writeParticle(p, i++); }
    this.live.sparks = live; this.live.dust = liveDust;
    const pg = this.points.geometry;
    (pg.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (pg.getAttribute('aColor') as THREE.BufferAttribute).needsUpdate = true;
    (pg.getAttribute('aSize') as THREE.BufferAttribute).needsUpdate = true;
    (pg.getAttribute('aShape') as THREE.BufferAttribute).needsUpdate = true;

    // İzler: yayarken her karede eklem noktası; Active bitince yayma durur, yaş > TRAIL_LIFE olan noktalar söner
    let liveTrails = 0;
    let seg = 0;
    for (const t of this.trails) {
      if (t.fighter === NO_ID) continue;
      const f = world.fighters[t.fighter];
      if (t.emitting && (!f || f.phase !== MovePhase.Active)) t.emitting = false;
      if (t.emitting && this.deps.jointWorld(t.fighter, t.joint, _p)) {
        const h = t.head; t.pts[h * 3] = _p.x; t.pts[h * 3 + 1] = _p.y; t.pts[h * 3 + 2] = _p.z; t.ages[h] = 0;
        t.head = (h + 1) % TRAIL_POINTS; t.count = Math.min(t.count + 1, TRAIL_POINTS);
      }
      let any = false;
      for (let k = 0; k < TRAIL_POINTS; k++) { t.ages[k] = (t.ages[k] ?? 0) + dt; if ((t.ages[k] ?? 1) < TRAIL_LIFE) any = true; }
      if (!any) { if (!t.emitting) { t.fighter = NO_ID; t.count = 0; } continue; }
      liveTrails++;
      _c.setHex(t.color);
      for (let k = 1; k < t.count; k++) {
        const a = (t.head - k - 1 + TRAIL_POINTS * 2) % TRAIL_POINTS, b = (t.head - k + TRAIL_POINTS * 2) % TRAIL_POINTS;
        const o = seg * 6, oc = seg * 8;
        this.lPos[o] = t.pts[a * 3]!; this.lPos[o + 1] = t.pts[a * 3 + 1]!; this.lPos[o + 2] = t.pts[a * 3 + 2]!;
        this.lPos[o + 3] = t.pts[b * 3]!; this.lPos[o + 4] = t.pts[b * 3 + 1]!; this.lPos[o + 5] = t.pts[b * 3 + 2]!;
        const aa = 1 - clamp01((t.ages[a] ?? 1) / TRAIL_LIFE), ab = 1 - clamp01((t.ages[b] ?? 1) / TRAIL_LIFE);
        this.lCol[oc] = _c.r; this.lCol[oc + 1] = _c.g; this.lCol[oc + 2] = _c.b; this.lCol[oc + 3] = aa;
        this.lCol[oc + 4] = _c.r; this.lCol[oc + 5] = _c.g; this.lCol[oc + 6] = _c.b; this.lCol[oc + 7] = ab;
        seg++;
      }
    }
    this.live.trails = liveTrails;
    for (let k = seg * 8; k < this.lCol.length; k += 4) this.lCol[k + 3] = 0;
    const lg = this.lines.geometry;
    (lg.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (lg.getAttribute('aColor') as THREE.BufferAttribute).needsUpdate = true;

    // Kenar halkaları: oyuncu < 2 u kırmızı; diğerleri < 1.5 u soluk (DESIGN §11/§14); tutulan/taşınan → kırmızı nabız
    let liveRings = 0;
    for (const f of world.fighters) {
      const alive = f.state !== FState.Eliminated && f.state !== FState.Respawning && f.state !== FState.Falling && this.deps.posOf(f.id, _p);
      const held = f.state === FState.Held || f.state === FState.Carried;
      const isPlayer = this.deps.isLocal ? this.deps.isLocal(f.id) : f.id === this.deps.playerId;
      const near = isPlayer ? f.edgeDist < T.EDGE.hudRingSelf : f.edgeDist < T.EDGE.hudRingOthers;
      if (!alive || (!near && !held)) { this.rings.setMatrixAt(f.id, _m.makeScale(0, 0, 0)); continue; }
      liveRings++;
      const pulse = 0.65 + 0.35 * Math.sin(this.time * (held ? 12 : 7));
      const radius = held ? 0.9 : 1.1;
      _c.setHex(COLOR_RED).multiplyScalar((isPlayer || held ? 0.9 : 0.35) * pulse);
      this.rings.setColorAt(f.id, _c);
      this.rings.setMatrixAt(f.id, _m.makeScale(radius, 1, radius).setPosition(_p.x, _p.y + 0.02, _p.z));
    }
    for (let k = MAX_FIGHTERS; k < RING_SLOTS; k++) {
      const r = this.ringSlots[k]!;
      if (r.kind === RingKind.None || r.life <= 0) { this.rings.setMatrixAt(k, _m.makeScale(0, 0, 0)); r.kind = RingKind.None; continue; }
      // Gecikmeli halka: slot ayrılmış ama henüz görünmez (varış vuruşu). Ömür saati gecikme bitince işler.
      if (r.delay > 0) { r.delay -= dt; this.rings.setMatrixAt(k, _m.makeScale(0, 0, 0)); continue; }
      r.life -= dt; liveRings++;
      const p = 1 - Math.max(0, r.life) / r.max;
      const radius = r.r0 + (r.r1 - r.r0) * p;
      _c.setHex(r.color).multiplyScalar(r.alpha * (r.mode === RingAlpha.Grow ? 0.3 + 0.7 * p : 1 - p));
      this.rings.setColorAt(k, _c);
      this.rings.setMatrixAt(k, _m.makeScale(radius, 1, radius).setPosition(r.x, r.y, r.z));
    }
    this.live.rings = liveRings;
    this.rings.instanceMatrix.needsUpdate = true; if (this.rings.instanceColor) this.rings.instanceColor.needsUpdate = true;

    // Sütunlar (havuz sahnede değilse hiç dokunma: matris/renk yazımı da boşa gitmesin)
    let liveCols = 0;
    if (this.columns.parent) for (let k = 0; k < COLUMN_SLOTS; k++) {
      const c = this.colSlots[k]!;
      if (c.life <= 0) { this.columns.setMatrixAt(k, _m.makeScale(0, 0, 0)); continue; }
      c.life -= dt; liveCols++;
      const p = 1 - Math.max(0, c.life) / c.max;
      _c.setHex(c.color).multiplyScalar(0.7 * (1 - p));
      this.columns.setColorAt(k, _c);
      this.columns.setMatrixAt(k, _m.makeScale(c.r * (1 + p * 0.5), c.h, c.r * (1 + p * 0.5)).setPosition(c.x, c.y, c.z));
    }
    this.live.columns = liveCols;
    this.columns.instanceMatrix.needsUpdate = true; if (this.columns.instanceColor) this.columns.instanceColor.needsUpdate = true;

    // Kırılma parçaları
    let liveFrags = 0;
    for (let k = 0; k < FRAG_COUNT; k++) {
      const f = this.fragSlots[k]!;
      if (f.life <= 0) { this.frags.setMatrixAt(k, _m.makeScale(0, 0, 0)); continue; }
      f.life -= dt; liveFrags++;
      f.vy += T.JUMP.gravity * dt; f.x += f.vx * dt; f.y += f.vy * dt; f.z += f.vz * dt; f.rx += 5 * dt; f.ry += 7 * dt;
      const s = Math.min(1, f.life / 0.2);
      _q.setFromEuler(_e.set(f.rx, f.ry, 0)); _s.set(s, s, s); _v.set(f.x, f.y, f.z);
      this.frags.setMatrixAt(k, _m.compose(_v, _q, _s));
    }
    this.live.frags = liveFrags;
    this.frags.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    for (const o of [this.points, this.lines, this.rings, this.columns, this.frags]) {
      o.removeFromParent();
      // InstancedMesh.dispose(): three 0.185 instanceMatrix/instanceColor GL tamponlarını yalnız 'dispose' olayıyla bırakır
      // (onInstancedMeshDispose); geometry/material dispose bunları kapsamaz → her rematch'te GPU'da birikirdi (arenaView kalıbı).
      if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose();
      (o as THREE.Mesh).geometry.dispose();
      ((o as THREE.Mesh).material as THREE.Material).dispose();
    }
  }
}
